import type { Image3D } from '~/domain/patient/image3d';
import type { SeriesStreamPlan } from '~/hooks/openInViewer/streamingPlan';
import type { StreamedSeries } from './SliceStreamController';

/**
 * Pairs each streaming plan with the volume the commit actually built for it.
 *
 * A plan with no progressive volume behind it is dropped rather than fetched:
 * the commit fell back to the parsed-series path, or built nothing at all, and
 * there would be nothing to insert the slices into. A volume that is somehow
 * already complete is dropped for the same reason.
 *
 * The image the viewer is showing streams first — it is the one the user is
 * looking at, and the plan's "start simple" ordering says primary first.
 *
 * @param images Volumes the commit produced.
 * @param plans The open's streaming plans, in resolution order.
 * @param primarySeriesUid Series UID of the image currently on screen.
 */
export function toStreamTargets(
    images: readonly Image3D[],
    plans: readonly SeriesStreamPlan[],
    primarySeriesUid: string | undefined
): StreamedSeries[] {
    const targets: StreamedSeries[] = [];
    for (const plan of plans) {
        const image = images.find(
            (candidate) =>
                candidate.seriesUid === plan.manifest.seriesInstanceUid &&
                candidate.isProgressivelyLoaded &&
                !candidate.isComplete
        );
        if (image) {
            targets.push({ image, addresses: plan.remaining });
        }
    }
    // Stable: only the primary is moved, everything else keeps plan order.
    return targets.sort(
        (a, b) =>
            (a.image.seriesUid === primarySeriesUid ? 0 : 1) -
            (b.image.seriesUid === primarySeriesUid ? 0 : 1)
    );
}
